import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, X, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExtractionHistoryFilters as FilterState, ExtractionStatus, DatePreset } from '../types/wiseboard';

interface Props {
  filters: FilterState;
  totalCount: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  availableGpus?: string[];
  onSearchChange: (search: string) => void;
  onStatusChange: (status: ExtractionStatus | null) => void;
  onGpuChange: (gpu: string | null) => void;
  onDatePresetChange: (preset: DatePreset) => void;
  onRefresh: () => void;
  onClearFilters: () => void;
}

const datePresets: { label: string; value: DatePreset }[] = [
  { label: 'Today', value: 'today' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' }
];

const statusOptions: { label: string; value: ExtractionStatus }[] = [
  { label: 'Pending', value: 'pending' },
  { label: 'Extracting', value: 'extracting' },
  { label: 'Extracted', value: 'extracted' },
  { label: 'Classifying', value: 'classifying' },
  { label: 'Classified', value: 'classified' },
  { label: 'Splitting', value: 'splitting' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' }
];

export function ExtractionHistoryFilters({
  filters,
  totalCount,
  page,
  pageSize,
  isLoading,
  availableGpus = ['gpu1', 'gpu2'],
  onSearchChange,
  onStatusChange,
  onGpuChange,
  onDatePresetChange,
  onRefresh,
  onClearFilters
}: Props) {
  const hasActiveFilters = filters.search || filters.status || filters.gpu;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  return (
    <div className="space-y-3">
      {/* Filter Row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search loan/borrower..."
            value={filters.search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {/* Status Dropdown */}
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(value) => onStatusChange(value === 'all' ? null : value as ExtractionStatus)}
        >
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {statusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* GPU Dropdown */}
        <Select
          value={filters.gpu ?? 'all'}
          onValueChange={(value) => onGpuChange(value === 'all' ? null : value)}
        >
          <SelectTrigger className="w-[100px] h-9">
            <SelectValue placeholder="GPU" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All GPUs</SelectItem>
            {availableGpus.map((gpu) => (
              <SelectItem key={gpu} value={gpu}>
                {gpu.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date Presets */}
        <div className="flex items-center bg-muted rounded-lg p-1">
          {datePresets.map((preset) => (
            <Button
              key={preset.value}
              variant="ghost"
              size="sm"
              className={cn(
                'px-2.5 py-1 text-xs font-medium rounded-md transition-all',
                filters.datePreset === preset.value
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => onDatePresetChange(preset.value)}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        {/* Refresh Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          className="h-9"
        >
          <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
        </Button>
      </div>

      {/* Active Filters + Count Row */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {/* Active Filter Tags */}
          {filters.status && (
            <Badge variant="secondary" className="gap-1 pr-1">
              Status: {filters.status}
              <button
                onClick={() => onStatusChange(null)}
                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.gpu && (
            <Badge variant="secondary" className="gap-1 pr-1">
              GPU: {filters.gpu}
              <button
                onClick={() => onGpuChange(null)}
                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.search && (
            <Badge variant="secondary" className="gap-1 pr-1">
              Search: {filters.search.length > 20 ? `${filters.search.slice(0, 20)}...` : filters.search}
              <button
                onClick={() => onSearchChange('')}
                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Clear all
            </Button>
          )}
        </div>

        {/* Count Display */}
        <span className="text-muted-foreground">
          {totalCount > 0 ? (
            <>Showing {startItem.toLocaleString()}-{endItem.toLocaleString()} of {totalCount.toLocaleString()}</>
          ) : (
            'No results'
          )}
        </span>
      </div>
    </div>
  );
}

export default ExtractionHistoryFilters;
